import type { RouteRecordRaw } from 'vue-router';
import { ErrorView } from '../views/ErrorView';
import { PerformanceCategory } from '../views/Performance';
import { routes } from './routes';

export const routeDefinitions: RouteRecordRaw[] = [
  // --- PUBLIC ROUTES ---
  {
    path: routes.support.path,
    name: routes.support.name,
    component: () => import('#views/Support/SupportView.vue'),
    meta: {
      isUnauthenticated: true,
      shouldDisabledInImpersonation: true
    }
  },
  {
    path: routes.error.path,
    name: routes.error.name,
    component: ErrorView,
    props: (route) => ({
      titleKey: (route.query.titleKey as string) || (history.state?.titleKey as string) || undefined,
      subtitleKey: (route.query.subtitleKey as string) || (history.state?.subtitleKey as string) || undefined,
      icon: (route.query.icon as string) || (history.state?.icon as string) || undefined
    }),
    meta: {
      isUnauthenticated: true
    }
  },
  {
    path: routes.authError.path,
    name: routes.authError.name,
    component: ErrorView,
    props: (route) => ({
      titleKey: (route.query.titleKey as string) || (history.state?.titleKey as string) || undefined,
      subtitleKey: (route.query.subtitleKey as string) || (history.state?.subtitleKey as string) || undefined,
      icon: (route.query.icon as string) || (history.state?.icon as string) || undefined
    }),
    meta: {
      isUnauthenticated: true
    }
  },

  // --- ONBOARDING (Special case: needs permission but not onboarding guard) ---
  {
    path: routes.onboarding.path,
    name: routes.onboarding.name,
    component: () => import('#views/Onboarding/OnboardingView.vue'),
    meta: {
      isOnboarding: true
    }
  },

  // --- PROTECTED ROUTES ---
  // Performance Group
  {
    path: routes.performance.path,
    name: routes.performance.name,
    component: PerformanceCategory,
    meta: {
      filterView: 'performance',
      breadcrumbs: [{ title: 'commonLabel.performance', to: { name: routes.performance.name } }]
    }
  },
  {
    path: routes.detail('market-share').path,
    name: routes.detail('market-share').name,
    component: () => import('#views/Performance/PerformanceMarketShareEmbedding.vue'),
    meta: {
      filterView: 'market-share',
      accessibleIfOneOf: ['hasAccessToNielsenMarketShare'],
      breadcrumbs: [
        { title: 'commonLabel.performance', to: { name: routes.performance.name } },
        { title: 'commonLabel.details', to: { name: routes.detail('market-share').name } }
      ]
    }
  },
  {
    path: routes.detail('sales-amount').path,
    name: routes.detail('sales-amount').name,
    component: () => import('#views/Performance/PerformanceDetail.vue'),
    props: { name: 'sales-amount' },
    meta: {
      filterView: 'sales-amount',
      accessibleIfOneOf: ['hasAccessToSalesAmount'],
      breadcrumbs: [
        { title: 'commonLabel.performance', to: { name: routes.performance.name } },
        { title: 'commonLabel.details', to: { name: routes.detail('sales-amount').name } }
      ]
    }
  },
  {
    path: routes.detail('sales-quantity').path,
    name: routes.detail('sales-quantity').name,
    component: () => import('#views/Performance/PerformanceDetail.vue'),
    props: { name: 'sales-quantity' },
    meta: {
      filterView: 'sales-quantity',
      accessibleIfOneOf: ['hasAccessToSalesQuantity'],
      breadcrumbs: [
        { title: 'commonLabel.performance', to: { name: routes.performance.name } },
        { title: 'commonLabel.details', to: { name: routes.detail('sales-quantity').name } }
      ]
    }
  },
  {
    path: routes.detail('possession-rate').path,
    name: routes.detail('possession-rate').name,
    component: () => import('#views/Performance/PossessionRate/PossessionRateDetail.vue'),
    meta: {
      filterView: 'possession-rate',
      accessibleIfOneOf: ['hasAccessToPossessionZoomPage', 'hasAccessToPossessionZoomPageWithTheoretical'],
      breadcrumbs: [
        { title: 'commonLabel.performance', to: { name: routes.performance.name } },
        { title: 'possessionZoomPage.title', to: { name: routes.detail('possession-rate').name } }
      ]
    }
  },
  {
    path: routes.detail('dynamic-client').path,
    name: routes.detail('dynamic-client').name,
    component: () => import('#views/Performance/DynamicClient/DynamicClient.vue'),
    meta: {
      filterView: 'dynamic-client',
      accessibleIfOneOf: ['hasAccessToDynamicClient'],
      breadcrumbs: [
        { title: 'commonLabel.performance', to: { name: routes.performance.name } },
        { title: 'dynamicClientZoomPage.title', to: { name: routes.detail('dynamic-client').name } }
      ]
    }
  },
  {
    path: routes.detail('dn-dv').path,
    name: routes.detail('dn-dv').name,
    component: () => import('#views/Performance/DnDv/DnDvDetail.vue'),
    meta: {
      filterView: 'dn-dv',
      accessibleIfOneOf: ['hasAccessToDnDvZoomPage'],
      breadcrumbs: [
        { title: 'commonLabel.performance', to: { name: routes.performance.name } },
        { title: 'dnDvZoomPage.title', to: { name: routes.detail('dn-dv').name } }
      ]
    }
  },
  {
    path: routes.businessEquation.path,
    name: routes.businessEquation.name,
    component: () => import('#views/Performance/BusinessEquation/BusinessEquationDetail.vue'),
    meta: {
      filterView: 'business-equation',
      accessibleIfOneOf: [
        'hasAccessToBusinessEquationAllCustomers',
        'hasAccessToBusinessEquationCardholdersNonCardholders'
      ],
      breadcrumbs: [
        { title: 'commonLabel.performance', to: { name: routes.performance.name } },
        { title: 'businessEquationZoom.title', to: { name: routes.businessEquation.name } }
      ]
    }
  },
  {
    path: routes.customerSegmentation.path,
    name: routes.customerSegmentation.name,
    component: () => import('#views/Performance/CustomerSegmentation/CustomerSegmentation.vue'),
    meta: {
      filterView: 'customer-segmentation',
      accessibleIfOneOf: ['hasAccessToCustomerSegmentation'],
      breadcrumbs: [
        { title: 'commonLabel.performance', to: { name: routes.performance.name } },
        { title: 'customerSegmentation.title', to: { name: routes.customerSegmentation.name } }
      ]
    }
  },

  // Competitor
  {
    path: routes.competitor.vision.path,
    name: routes.competitor.vision.name,
    component: () => import('#views/CompetitorVision/CompetitorVision.vue'),
    meta: {
      filterView: 'competitor-vision',
      accessibleIfOneOf: ['hasAccessToCompetitorVision'],
      breadcrumbs: [
        { title: 'commonLabel.competitor', to: { name: routes.performance.name } },
        { title: 'commonLabel.competitor.vision', to: { name: routes.competitor.vision.name } }
      ]
    }
  },

  // Projects
  {
    path: routes.projects.list.path,
    name: routes.projects.list.name,
    component: () => import('#views/Projects/ProjectView.vue'),
    meta: {
      requiredPermission: 'isProjectsAuthorized',
      breadcrumbs: [{ title: 'commonLabel.projects', to: { name: routes.projects.list.name } }]
    }
  },
  {
    path: routes.projects.details.path,
    name: routes.projects.details.name,
    component: () => import('#views/Projects/ProjectDetailsView.vue'),
    meta: {
      requiredPermission: 'isProjectsAuthorized',
      breadcrumbs: [{ title: 'commonLabel.projects', to: { name: 'projects list' } }]
    }
  },

  // Administration Group
  {
    path: routes.admin.usageTracking.path,
    name: routes.admin.usageTracking.name,
    component: () => import('#views/Projects/UsageTrackingView.vue'),
    meta: { isProjectsAdminRequired: true }
  },
  {
    path: routes.admin.home.path,
    name: routes.admin.home.name,
    component: () => import('#views/Admin/AdminView.vue'),
    meta: {
      requiredPermission: 'isAdmin',
      breadcrumbs: [{ title: 'commonLabel.administration' }]
    }
  },
  {
    path: routes.admin.account.path,
    name: routes.admin.account.name,
    component: () => import('#views/AccountManagement/AccountManagement.vue'),
    meta: {
      requiredPermission: 'isAccountManager',
      breadcrumbs: [{ title: 'commonLabel.administration' }]
    }
  },
  {
    path: routes.admin.organizationsManagementList.path,
    name: routes.admin.organizationsManagementList.name,
    component: () => import('#views/OrganizationAccountCreation/OrganizationListView.vue'),
    meta: {
      requiredPermission: 'isOrganizationsCreator',
      breadcrumbs: [
        { title: 'commonLabel.administration' },
        { title: 'commonLabel.administration.organizationCreation' }
      ]
    }
  },
  {
    path: routes.admin.organizationsManagementCreate.path,
    name: routes.admin.organizationsManagementCreate.name,
    component: () => import('#views/OrganizationAccountCreation/CreateOrganizationView/CreateOrganizationView.vue'),
    meta: {
      requiredPermission: 'isOrganizationsCreator',
      breadcrumbs: [
        { title: 'commonLabel.administration' },
        { title: 'commonLabel.administration.organizationCreation' }
      ]
    }
  },
  {
    path: routes.admin.organizationManagementUpdate().path,
    name: routes.admin.organizationManagementUpdate().name,
    component: () => import('#views/OrganizationAccountCreation/UpdateOrganizationView/UpdateOrganizationView.vue'),
    meta: {
      requiredPermission: 'isOrganizationsCreator',
      breadcrumbs: [
        { title: 'commonLabel.administration' },
        { title: 'commonLabel.administration.organizationCreation' }
      ]
    }
  },
  {
    path: routes.admin.permissionsManagement.path,
    redirect: { name: 'permissionsManagementHoldings' },
    component: () => import('#views/Admin/PermissionsManagement/PermissionsManagement.vue'),
    children: [
      {
        path: routes.admin.permissionsManagementHoldings.path,
        name: routes.admin.permissionsManagementHoldings.name,
        component: () => import('#views/Admin/PermissionsManagement/PermissionsManagementHoldings.vue'),
        meta: {
          requiredPermission: 'isOrganizationsCreator',
          breadcrumbs: [
            {
              icon: 'settings',
              title: 'commonLabel.administrationFull'
            },
            {
              icon: 'trello',
              title: 'commonLabel.administration.holding'
            }
          ]
        },
        children: [
          {
            path: routes.admin.permissionsManagementHoldingsCreate.path,
            name: routes.admin.permissionsManagementHoldingsCreate.name,
            component: () => import('#views/Admin/components/holdingForm/HoldingForm.vue'),
            meta: {
              requiredPermission: 'isOrganizationsCreator',
              breadcrumbs: [
                {
                  icon: 'settings',
                  title: 'commonLabel.administrationFull'
                },
                {
                  icon: 'trello',
                  title: 'commonLabel.administration.holding'
                },
                {
                  icon: 'plus',
                  title: 'commonLabel.create'
                }
              ]
            }
          },
          {
            path: routes.admin.permissionsManagementHoldingsEdit.path,
            name: routes.admin.permissionsManagementHoldingsEdit.name,
            component: () => import('#views/Admin/components/holdingForm/HoldingForm.vue'),
            props: true,
            meta: {
              requiredPermission: 'isOrganizationsCreator',
              breadcrumbs: [
                {
                  icon: 'settings',
                  title: 'commonLabel.administrationFull'
                },
                {
                  icon: 'trello',
                  title: 'commonLabel.administration.holding'
                },
                {
                  icon: 'edit',
                  title: 'commonLabel.edit'
                }
              ]
            }
          }
        ]
      },
      {
        path: routes.admin.permissionsManagementOffers.path,
        name: routes.admin.permissionsManagementOffers.name,
        component: () => import('#views/Admin/PermissionsManagement/PermissionsManagementOffers.vue'),
        meta: {
          requiredPermission: 'isAdmin',
          breadcrumbs: [
            { icon: 'settings', title: 'commonLabel.administrationFull' },
            { icon: 'pie-chart', title: 'commonLabel.administration.offers' }
          ]
        }
      }
    ]
  },

  // Misc
  {
    path: routes.tools.path,
    name: routes.tools.name,
    component: () => import('#views/Tools/ToolsView.vue')
  },
  {
    path: routes.settings.home.path,
    name: routes.settings.home.name,
    component: () => import('#views/Settings/SettingsView.vue')
  },
  {
    path: routes.settings.edit.path,
    name: routes.settings.edit.name,
    component: () => import('#views/Settings/SettingsEditView.vue')
  },
  {
    path: routes.downloadBucketFile.path,
    name: routes.downloadBucketFile.name,
    component: () => import('#views/DownloadBucketFile/DownloadBucketFileView.vue'),
    props: true
  }
];
